import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ImageLightbox } from '@/components/shared/image-lightbox';
import {
  canChooseExtendedMemberPreview,
  useMemberQuickViewPreviewMode,
} from '@/components/shared/member-quick-view-preview-mode';
import { MemberQuickViewPreviewToggle } from '@/components/shared/member-quick-view-preview-toggle';
import {
  MemberQuickViewContent,
  MemberQuickViewLoading,
  displayMemberName,
  type MemberQuickViewRecord,
} from '@/components/shared/member-quick-view-content';
import { ErrorState, PermissionDeniedState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MemberPhoto } from '@/components/ui/member-photo';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-client';
import { useChurchStore } from '@/stores/church-store';
import { cn } from '@/utils/cn';

import { memberDetailPath, type MembersListState } from './members-list-state';

const resolveErrorStatus = (error: unknown): number | null =>
  error instanceof ApiError ? error.status : null;

const PanelHeaderPhoto = ({
  loading,
  row,
  name,
  onExpand,
}: {
  loading: boolean;
  row: MemberQuickViewRecord | undefined;
  name: string;
  onExpand: () => void;
}) => {
  const { t } = useTranslation('people');

  if (loading) {
    return <Skeleton className="h-10 w-[30px] shrink-0 rounded-sm" />;
  }
  if (!row) {
    return null;
  }
  if (row.photoUrl) {
    return (
      <button
        type="button"
        className="shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onExpand}
        aria-label={t('members.quickView.expandPhoto', { name })}
      >
        <MemberPhoto src={row.photoUrl} name={name} size="sm" />
      </button>
    );
  }
  return <MemberPhoto src={null} name={name} size="sm" className="shrink-0" />;
};

export const MemberQuickViewPanel = ({
  memberId,
  previewName,
  listState,
  className,
}: {
  memberId: string | null;
  previewName?: string;
  listState: MembersListState;
  className?: string;
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const [photoLightboxOpen, setPhotoLightboxOpen] = useState(false);

  useEffect(() => {
    setPhotoLightboxOpen(false);
  }, [memberId]);
  const membership = useCurrentMembership();
  const churchId = useChurchStore((state) => state.church?.id ?? null);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);

  const canViewContact = hasPermission(membership, PERMISSIONS.PEOPLE_VIEW_CONTACT);
  const canViewProfile =
    isModuleEnabled('people') && hasPermission(membership, PERMISSIONS.PEOPLE_VIEW);
  const canChooseExtended = canChooseExtendedMemberPreview(membership);
  const { extendedPreview, setExtendedPreview } = useMemberQuickViewPreviewMode(churchId);
  const previewMode = canChooseExtended && extendedPreview ? 'extended' : 'basic';
  const isExtendedLayout = previewMode === 'extended';

  const member = useQuery({
    queryKey: queryKeys.detail(churchId, 'people/members', memberId ?? ''),
    queryFn: () => api.get<MemberQuickViewRecord>(`/people/members/${memberId}`),
    enabled: Boolean(memberId),
  });

  if (!memberId) {
    return (
      <Card className={cn('flex h-full min-h-[20rem] flex-col', className)}>
        <CardContent className="flex flex-1 items-center justify-center p-6">
          <p className="text-center text-body-sm text-fg-secondary">{t('members.preview.empty')}</p>
        </CardContent>
      </Card>
    );
  }

  const row = member.data;
  const name = row ? displayMemberName(row) : (previewName?.trim() || t('members.quickView.title'));
  const errorStatus = resolveErrorStatus(member.error);

  let body = null;
  if (member.isLoading) {
    body = <MemberQuickViewLoading layout="panel" />;
  } else if (errorStatus === 403) {
    body = (
      <PermissionDeniedState
        title={t('members.quickView.deniedTitle')}
        body={t('members.quickView.deniedBody')}
        className="py-6"
      />
    );
  } else if (errorStatus === 404) {
    body = <ErrorState title={t('members.quickView.unavailable')} className="py-6" />;
  } else if (member.error) {
    body = (
      <ErrorState
        title={t('members.quickView.loadError')}
        onRetry={() => void member.refetch()}
        retryLabel={tCommon('action.retry')}
        className="py-6"
      />
    );
  } else if (row) {
    body = (
      <MemberQuickViewContent
        row={row}
        name={name}
        canViewContact={canViewContact}
        layout="panel"
        previewMode={previewMode}
      />
    );
  }

  return (
    <div className={cn(isExtendedLayout && 'h-[calc(100dvh-30rem)]')}>
      <Card
        className={cn(
          isExtendedLayout
            ? 'grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden'
            : 'flex flex-col',
          className,
        )}
      >
      <CardHeader className="shrink-0 border-b border-border pb-4">
        <div className="flex items-start gap-3">
          <PanelHeaderPhoto
            loading={member.isLoading}
            row={row}
            name={name}
            onExpand={() => setPhotoLightboxOpen(true)}
          />
          <div className="min-w-0 flex-1">
            <CardTitle className="text-body font-medium">{name}</CardTitle>
            {row ? (
              <p className="text-body-sm text-fg-secondary">
                {t(`members.type.${row.memberType}`)} · {t(`members.status.${row.status}`)}
              </p>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent
        className={cn('p-4', isExtendedLayout ? 'min-h-0 overflow-y-auto pb-6' : undefined)}
      >
        {body}
      </CardContent>
      {row && (canViewProfile || canChooseExtended) ? (
        <div
          className={cn(
            'flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-t border-border p-4',
            canChooseExtended ? 'justify-between' : 'justify-end',
          )}
        >
          {canChooseExtended ? (
            <MemberQuickViewPreviewToggle
              id="member-preview-panel-extended"
              checked={extendedPreview}
              onCheckedChange={setExtendedPreview}
            />
          ) : null}
          {canViewProfile ? (
            <Button asChild>
              <Link to={memberDetailPath(row.id, listState)}>{t('members.quickView.viewProfile')}</Link>
            </Button>
          ) : null}
        </div>
      ) : null}
      {row?.photoUrl ? (
        <ImageLightbox
          images={[row.photoUrl]}
          index={photoLightboxOpen ? 0 : null}
          onClose={() => setPhotoLightboxOpen(false)}
          onIndexChange={() => {}}
          caption={name}
          labels={{
            dialogLabel: tCommon('lightbox.title'),
            close: tCommon('lightbox.close'),
            previous: tCommon('lightbox.previous'),
            next: tCommon('lightbox.next'),
            count: tCommon('lightbox.imageCount', { current: 1, total: 1 }),
            imageAlt: name,
          }}
        />
      ) : null}
    </Card>
    </div>
  );
};
